import type { ErrorInfo } from 'react';
import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';

// ============================================================================
// TYPES
// ============================================================================

export interface ErrorReportData {
  errorId: string;
  timestamp: number;
  error: Error;
  errorInfo: ErrorInfo;
  context?: Record<string, unknown>;
  userAgent: string;
  url: string;
}

export interface Breadcrumb {
  type: 'navigation' | 'api-request' | 'api-error' | 'user-action';
  timestamp: number;
  message: string;
  data?: Record<string, string | number | boolean>;
}

// ============================================================================
// BREADCRUMBS BUFFER
// ============================================================================

const MAX_BREADCRUMBS = 20;
const breadcrumbs: Breadcrumb[] = [];

export function addBreadcrumb(crumb: Breadcrumb): void {
  breadcrumbs.push(crumb);
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...breadcrumbs];
}

// ============================================================================
// APP INSIGHTS SINGLETON
// ============================================================================

let appInsights: ApplicationInsights | null = null;

function getAppInsights(): ApplicationInsights | null {
  if (appInsights) return appInsights;

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
  if (!connectionString) {
    return null;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,

      // Distributed tracing: отправляем traceparent header на бэкенд
      enableCorsCorrelation: true,
      correlationHeaderExcludedDomains: [],

      // Включаем fetch tracking для автоматической отправки traceparent
      disableFetchTracking: false,
      disableAjaxTracking: true,

      // Отключаем автоматический dependency tracking — мы трекаем вручную через api.ts
      maxAjaxCallsPerView: 0,
    },
  });

  // Telemetry initializer: обогащаем каждое событие метаданными
  appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
    item.tags = item.tags || [];
    item.tags['ai.cloud.role'] = 'plng-frontend';

    const customProps = item.data || {};
    customProps['app_version'] = __APP_VERSION__;
    customProps['environment'] = import.meta.env.MODE;
    item.data = customProps;
  });

  appInsights.loadAppInsights();
  return appInsights;
}

// ============================================================================
// USER CONTEXT
// ============================================================================

/**
 * Устанавливает контекст авторизованного пользователя.
 * Вызывается из AuthBootstrap после успешной авторизации.
 */
export function setTelemetryUser(userId: string, accountName?: string): void {
  const insights = getAppInsights();
  if (!insights) return;

  insights.setAuthenticatedUserContext(userId, accountName, true);
}

/**
 * Очищает контекст пользователя (при logout).
 */
export function clearTelemetryUser(): void {
  const insights = getAppInsights();
  if (!insights) return;

  insights.clearAuthenticatedUserContext();
}

// ============================================================================
// ERROR REPORTING
// ============================================================================

export function generateErrorId(): string {
  return `ERROR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Отправляет отчет об ошибке в Application Insights.
 * Включает breadcrumbs — последние действия пользователя перед ошибкой.
 */
export function reportErrorToService(data: ErrorReportData): void {
  if (import.meta.env.DEV) {
    console.error('Reporting error to monitoring service:', {
      errorId: data.errorId,
      timestamp: new Date(data.timestamp).toISOString(),
      message: data.error.message,
      stack: data.error.stack,
      componentStack: data.errorInfo.componentStack,
      context: data.context,
      breadcrumbs: getBreadcrumbs(),
    });
  }

  const insights = getAppInsights();
  if (!insights) return;

  const recentBreadcrumbs = getBreadcrumbs();
  const breadcrumbsSerialized = JSON.stringify(recentBreadcrumbs);

  insights.trackException({
    exception: data.error,
    properties: {
      errorId: data.errorId,
      timestamp: new Date(data.timestamp).toISOString(),
      componentStack: data.errorInfo.componentStack || '',
      userAgent: data.userAgent,
      url: data.url,
      breadcrumbs: breadcrumbsSerialized,
      route: window.location.pathname,
      ...data.context,
    },
  });
}

// ============================================================================
// CUSTOM EVENTS
// ============================================================================

/**
 * Трекинг API запроса как dependency + breadcrumb.
 * Вызывается из api.ts после каждого запроса.
 */
export function trackApiCall(data: {
  method: string;
  url: string;
  duration: number;
  success: boolean;
  statusCode?: number;
  correlationId: string;
}): void {
  addBreadcrumb({
    type: data.success ? 'api-request' : 'api-error',
    timestamp: Date.now(),
    message: `${data.method} ${data.url} → ${data.statusCode ?? 'network error'}`,
    data: {
      method: data.method,
      duration: data.duration,
      success: data.success,
      correlationId: data.correlationId,
    },
  });

  const insights = getAppInsights();
  if (!insights) return;

  insights.trackDependencyData({
    id: data.correlationId,
    name: `${data.method} ${data.url}`,
    duration: data.duration,
    success: data.success,
    responseCode: data.statusCode ?? 0,
    type: 'HTTP',
    target: new URL(data.url, window.location.origin).host,
    data: data.url,
    properties: {
      correlationId: data.correlationId,
    },
  });
}

// ============================================================================
// UTILITIES
// ============================================================================

export function prepareErrorReport(
  error: Error | null,
  errorInfo: ErrorInfo | null,
  errorId: string,
  timestamp: number,
  context?: Record<string, unknown>
): Record<string, unknown> {
  return {
    errorId,
    timestamp: new Date(timestamp).toISOString(),
    message: error?.message || 'Unknown error',
    stack: error?.stack,
    componentStack: errorInfo?.componentStack,
    context,
    breadcrumbs: getBreadcrumbs(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
}
